import { useState } from "react";
import { Phone, MapPin, CheckCircle2, Loader2, Mail, Clock } from "lucide-react";
import { api, apiError } from "../lib/api";
import { BUSINESS } from "../data/site";

const SERVICES = ["Smartphone Sales", "Wireless Activations", "Phone Upgrades", "Wireless Plans", "Accessories", "Technical Support", "Other"];

export default function Contact() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", preferred_contact: "phone", service: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/inquiries", form);
            setDone(true);
        } catch (err) {
            setError(apiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="relative overflow-hidden">
            <div className="hero-glow" />
            <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <div className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold">Contact</div>
                    <h1 className="mt-3 font-display font-extrabold text-slate-900 text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Tell us how we can help.
                    </h1>
                    <p className="mt-5 text-slate-600 leading-relaxed max-w-xl">
                        Send a quick note and we'll get back to you within one business day — or call us right now and we'll pick up.
                    </p>

                    {done ? (
                        <div data-testid="contact-success" className="mt-8 rounded-3xl bg-emerald-50 border border-emerald-200 p-7 flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-display font-bold text-slate-900 text-lg">Thanks, {form.name.split(" ")[0]}!</div>
                                <div className="text-slate-600 text-sm mt-1">Your inquiry was received. We'll reach out via {form.preferred_contact === "email" ? "email" : "phone"} shortly.</div>
                                <button
                                    onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", message: "", preferred_contact: "phone", service: "" }); }}
                                    className="mt-4 text-sm font-semibold text-emerald-700 hover:underline"
                                >
                                    Send another inquiry
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} data-testid="contact-form" className="mt-8 bg-white rounded-3xl border border-slate-100 premium-shadow p-7 grid sm:grid-cols-2 gap-5">
                            <Field label="Full name" required>
                                <input data-testid="contact-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Maria Lopez" />
                            </Field>
                            <Field label="Phone" required>
                                <input data-testid="contact-phone" required value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(818) 555-1234" />
                            </Field>
                            <Field label="Email" required className="sm:col-span-2">
                                <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@example.com" />
                            </Field>
                            <Field label="Service needed">
                                <select data-testid="contact-service" value={form.service} onChange={update("service")} className={inputCls}>
                                    <option value="">Choose one…</option>
                                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </Field>
                            <Field label="Preferred contact method">
                                <select data-testid="contact-preferred" value={form.preferred_contact} onChange={update("preferred_contact")} className={inputCls}>
                                    <option value="phone">Phone call</option>
                                    <option value="email">Email</option>
                                </select>
                            </Field>
                            <Field label="Message" required className="sm:col-span-2">
                                <textarea data-testid="contact-message" required value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us a little about what you need help with…" />
                            </Field>
                            {error && (
                                <div data-testid="contact-error" className="sm:col-span-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                                    {error}
                                </div>
                            )}
                            <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3">
                                <button data-testid="contact-submit-btn" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-70 transition">
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    Submit Inquiry
                                </button>
                                <a href={`tel:${BUSINESS.phoneRaw}`} data-testid="contact-call-btn" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition">
                                    <Phone className="w-4 h-4" /> Call Now
                                </a>
                            </div>
                        </form>
                    )}
                </div>

                <aside className="lg:col-span-5 space-y-5">
                    <div className="bg-slate-900 text-white rounded-3xl p-7 premium-shadow">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">Visit us</div>
                        <div className="mt-3 space-y-4 text-sm">
                            <Row icon={<MapPin className="w-4 h-4 text-cyan-400" />}>
                                {BUSINESS.address}<br /><span className="text-white/50">Inside {BUSINESS.locatedInside}</span>
                            </Row>
                            <Row icon={<Phone className="w-4 h-4 text-cyan-400" />}>
                                <a href={`tel:${BUSINESS.phoneRaw}`} className="hover:underline">{BUSINESS.phone}</a>
                            </Row>
                            <Row icon={<Mail className="w-4 h-4 text-cyan-400" />}>info@egwireless.com</Row>
                            <Row icon={<Clock className="w-4 h-4 text-cyan-400" />}>
                                Mon–Fri 12–5 · Sat 12–4 · Sun 12–4
                            </Row>
                        </div>
                        <a href={BUSINESS.mapsDirections} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition w-full justify-center">
                            <MapPin className="w-4 h-4" /> Get Directions
                        </a>
                    </div>
                    <div className="rounded-3xl overflow-hidden premium-shadow">
                        <iframe title="map" src={BUSINESS.mapsEmbed} className="w-full h-72 border-0" loading="lazy" />
                    </div>
                </aside>
            </div>
        </section>
    );
}

const inputCls = "w-full h-11 px-4 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition";

function Field({ label, required, children, className }) {
    return (
        <label className={`block ${className || ""}`}>
            <span className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                {label}{required && <span className="text-red-500"> *</span>}
            </span>
            {children}
        </label>
    );
}

function Row({ icon, children }) {
    return (
        <div className="flex items-start gap-3 text-white/80">
            <div className="mt-0.5">{icon}</div>
            <div className="flex-1">{children}</div>
        </div>
    );
}
